import { useState, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import { Trophy, MessageCircle, Lock, ChevronLeft, ChevronRight } from 'lucide-react';

// Define the animation
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

interface Creator {
  id: string;
  firstName: string;
  lastName: string;
  tiktokHandle: string;
  tiktokViews: number;
  tiktokPosts: number;
  tiktokComments: number;
  rank: number;
}

// Format numbers with K/M suffix
const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) {
    return '0';
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

export function TikTokLeaderboard() {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [hasDataForPreviousMonth, setHasDataForPreviousMonth] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('leaderboardToken');
    if (storedToken) {
      // Validate the token before setting authentication
      fetch('/api/public/leaderboards/validate', {
        headers: {
          'Authorization': `Bearer ${storedToken}`
        }
      })
      .then(response => {
        if (response.ok) {
          setAuthToken(storedToken);
          setIsAuthenticated(true);
        } else {
          // Clear invalid token
          localStorage.removeItem('leaderboardToken');
          setAuthToken(null);
          setIsAuthenticated(false);
        }
      })
      .catch(() => {
        // Clear token on error
        localStorage.removeItem('leaderboardToken');
        setAuthToken(null);
        setIsAuthenticated(false);
      });
    }
  }, []);

  // Save token to localStorage when it changes
  useEffect(() => {
    if (authToken) {
      localStorage.setItem('leaderboardToken', authToken);
    } else {
      localStorage.removeItem('leaderboardToken');
    }
  }, [authToken]);

  useEffect(() => {
    // Only fetch if authenticated
    if (isAuthenticated && authToken) {
      const fetchLeaderboard = async () => {
        try {
          const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
          const response = await fetch(`/api/public/leaderboards/tiktok?month=${monthStr}`, {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          if (!response.ok) {
            throw new Error('Failed to fetch leaderboard data');
          }
          const { data, lastUpdated } = await response.json();
          setCreators(data);
          setLastUpdated(lastUpdated ? new Date(lastUpdated) : null);
          
          // Check if there's data for the previous month
          const prevDate = new Date(currentMonth);
          prevDate.setMonth(prevDate.getMonth() - 1);
          const prevMonthStr = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
          const prevMonthResponse = await fetch(`/api/public/leaderboards/tiktok?month=${prevMonthStr}`, {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          if (prevMonthResponse.ok) {
            const { data: prevData } = await prevMonthResponse.json();
            setHasDataForPreviousMonth(prevData.length > 0);
          } else {
            setHasDataForPreviousMonth(false);
          }
        } catch (err) {
          console.error('Error fetching leaderboard:', err);
          setError(err instanceof Error ? err.message : 'An error occurred');
          // If we get a 401, clear the token
          if (err instanceof Error && err.message.includes('401')) {
            setAuthToken(null);
            setIsAuthenticated(false);
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchLeaderboard();
    }
  }, [authToken, isAuthenticated, currentMonth]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/public/leaderboards/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error('Invalid password');
      }

      const { token } = await response.json();
      setAuthToken(token);
      setIsAuthenticated(true);
      setPassword('');
    } catch (err) {
      alert('Incorrect password');
      setPassword('');
    }
  };

  const handlePreviousMonth = () => {
    if (!hasDataForPreviousMonth) return;
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    const now = new Date();
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      // Don't allow going past current month
      return newDate > now ? prev : newDate;
    });
  };

  const isCurrentMonth = () => {
    const now = new Date();
    return currentMonth.getMonth() === now.getMonth() && 
           currentMonth.getFullYear() === now.getFullYear();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <Lock className="mx-auto h-12 w-12 text-[#F7E8CC]" />
            <h1 className="mt-6 text-3xl font-bold text-[#F7E8CC]">
              Protected Leaderboard
            </h1>
          </div>
          <form onSubmit={handlePasswordSubmit} className="mt-8 space-y-6">
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded relative block w-full px-3 py-2 border border-[#F7E8CC]/20 bg-[#6E1912] placeholder-[#F7E8CC]/50 text-[#F7E8CC] focus:outline-none focus:ring-2 focus:ring-[#F7E8CC]/50 focus:border-transparent"
                  placeholder="Enter password"
                />
              </div>
            </div>
            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-[#7B1C14] bg-[#F7E8CC] hover:bg-[#F7E8CC]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F7E8CC]/50"
              >
                Access Leaderboard
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-[#F7E8CC] mb-4">
              TikTok Creator Leaderboard
            </h1>
          </div>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F7E8CC]" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-[#F7E8CC] mb-4">
              TikTok Creator Leaderboard
            </h1>
          </div>
          <div className="bg-[#6E1912] rounded-lg p-6 text-center">
            <p className="text-[#F7E8CC]">Failed to load leaderboard: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#7B1C14] flex flex-col items-center justify-center py-4 sm:py-8 lg:py-12 px-4 sm:px-8 lg:px-12 font-sans">
      <div className="w-full max-w-5xl flex flex-col flex-grow justify-center">
        {/* Header Section */}
        <div className="text-center mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#F7E8CC] mb-2 sm:mb-4">
            Spicy Cubes Leaderboard
          </h1>
          <div className="flex items-center justify-center space-x-4 mb-2">
            <button
              type="button"
              onClick={handlePreviousMonth}
              className={`p-1 ${!hasDataForPreviousMonth ? 'text-[#F7E8CC]/40 cursor-not-allowed' : 'text-[#F7E8CC]/80 hover:text-[#F7E8CC] transition-colors'}`}
              disabled={!hasDataForPreviousMonth}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <p className="text-sm sm:text-base text-[#F7E8CC]/80 min-w-[150px]">
              {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </p>
            <button
              type="button"
              onClick={handleNextMonth}
              className={`p-1 ${isCurrentMonth() ? 'text-[#F7E8CC]/40 cursor-not-allowed' : 'text-[#F7E8CC]/80 hover:text-[#F7E8CC] transition-colors'}`}
              disabled={isCurrentMonth()}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
          <p className="text-sm sm:text-base text-[#F7E8CC]/80">
            Top performing creators ranked by total views
          </p>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-[#6E1912] rounded-lg shadow-xl overflow-hidden mx-1 px-2 sm:px-4">
          <div className="overflow-x-auto">
            <div className="overflow-y-auto" style={{ height: 'calc(3.5rem * 10 + 3.5rem)' }}>
              <table className="w-full">
                <thead className="sticky top-0 bg-[#6E1912] z-10">
                  <tr className="border-b border-[#F7E8CC]/10">
                    <th className="h-14 px-3 sm:px-4 lg:px-6 text-left text-xs sm:text-sm font-semibold text-[#F7E8CC]">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <Trophy className="w-3 h-3 sm:w-4 sm:h-4" />
                      </div>
                    </th>
                    <th className="hidden md:table-cell h-14 px-3 sm:px-4 lg:px-6 text-left text-xs sm:text-sm font-semibold text-[#F7E8CC]">
                      Creator Name
                    </th>
                    <th className="h-14 px-3 sm:px-4 lg:px-6 text-left text-xs sm:text-sm font-semibold text-[#F7E8CC]">
                      TikTok Handle
                    </th>
                    <th className="h-14 px-3 sm:px-4 lg:px-6 text-left text-xs sm:text-sm font-semibold text-[#F7E8CC]">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <span>Views</span>
                      </div>
                    </th>
                    <th className="h-14 px-3 sm:px-4 lg:px-6 text-left text-xs sm:text-sm font-semibold text-[#F7E8CC]">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <span>Posts</span>
                      </div>
                    </th>
                    <th className="hidden md:table-cell h-14 px-3 sm:px-4 lg:px-6 text-left text-xs sm:text-sm font-semibold text-[#F7E8CC]">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Comments</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {creators.map((creator) => (
                    <tr 
                      key={creator.id}
                      className={`
                        border-b border-[#F7E8CC]/10 hover:bg-[#5E150F] transition-colors
                        ${creator.rank <= 3 ? 'bg-[#5E150F]/50' : ''}
                      `}
                    >
                      <td className="h-14 px-3 sm:px-4 lg:px-6 text-left">
                        <div className="flex items-center">
                          <span className={`
                            inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full text-sm sm:text-base
                            ${creator.rank === 1 ? 'bg-yellow-500/20 text-yellow-400' : 
                              creator.rank === 2 ? 'bg-gray-400/20 text-gray-300' :
                              creator.rank === 3 ? 'bg-amber-700/20 text-amber-600' :
                              'text-[#F7E8CC]/80'}
                          `}>
                            {creator.rank}
                          </span>
                        </div>
                      </td>
                      <td className="hidden md:table-cell h-14 px-3 sm:px-4 lg:px-6 text-left">
                        <div className="flex items-center">
                          <span className="font-medium text-[#F7E8CC] text-sm sm:text-base">
                            {creator.firstName} {creator.lastName ? `${creator.lastName[0]}.` : ''}
                          </span>
                        </div>
                      </td>
                      <td className="h-14 px-3 sm:px-4 lg:px-6 text-left">
                        <div className="flex items-center">
                          <a 
                            href={`https://tiktok.com/@${creator.tiktokHandle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-[#F7E8CC]/80 hover:text-[#F7E8CC] transition-colors text-sm sm:text-base break-words line-clamp-2"
                          >
                            {creator.tiktokHandle.startsWith('@') ? creator.tiktokHandle : `@${creator.tiktokHandle}`}
                          </a>
                        </div>
                      </td>
                      <td className="h-14 px-3 sm:px-4 lg:px-6 text-left text-[#F7E8CC]/80 text-sm sm:text-base">
                        {formatNumber(creator.tiktokViews)}
                      </td>
                      <td className="h-14 px-3 sm:px-4 lg:px-6 text-left text-[#F7E8CC]/80 text-sm sm:text-base">
                        {formatNumber(creator.tiktokPosts)}
                      </td>
                      <td className="hidden md:table-cell h-14 px-3 sm:px-4 lg:px-6 text-left text-[#F7E8CC]/80 text-sm sm:text-base">
                        {formatNumber(creator.tiktokComments)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div className="w-full text-center mt-6 sm:mt-8 lg:mt-10 pb-2 text-[#F7E8CC]/40 text-xs">
        Last updated: {lastUpdated ? lastUpdated.toLocaleString('en-US', { 
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }) : 'Never'}
      </div>
    </div>
  );
} 
